/**
 * @file index.tsx
 * @module app/modules/inward/index
 * @description Route entry for the UProcure Inward module, mounted at /UProcure/inward.
 *   Procurement (New) renders the same list from its own tab shell. Users without any
 *   Inward permission see a no-access message.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PageLink, PageTitle } from "../../../_admin/layout/core";
import { useAuth } from "../auth";
import InwardPoList from "./components/inward-po-list";
import InwardGrnForm from "./components/inward-grn-form";
import InwardGrnList from "./components/inward-grn-list";
import InwardGrnApproval from "./components/inward-grn-approval";
import InwardReport from "./components/inward-report";
import { InwardNoAccess } from "./components/inward-no-access";
import { canPerformInwardAction, InwardAction } from "./utils/inward-access";

export const INWARD_UPROCURE_BASE_PATH = "/UProcure/inward";

const inwardBreadCrumbs: Array<PageLink> = [
  { title: "UProcure", path: "/UProcure", isSeparator: false, isActive: false },
  { title: "", path: "", isSeparator: true, isActive: false },
];

export { InwardNoAccess };

/** Renders children only for users with any Inward access. */
export const InwardViewGuard = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth();
  return canPerformInwardAction(currentUser, InwardAction.View) ? (
    <>{children}</>
  ) : (
    <InwardNoAccess />
  );
};

/** Renders children only for users allowed to create a GRN. */
export const InwardCreateGrnGuard = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth();
  return canPerformInwardAction(currentUser, InwardAction.CreateGRN) ? (
    <>{children}</>
  ) : (
    <InwardNoAccess />
  );
};

const InwardPage = () => {
  const { currentUser } = useAuth();
  if (!canPerformInwardAction(currentUser, InwardAction.View)) {
    return <InwardNoAccess />;
  }
  return (
    <Routes>
      <Route
        index
        element={
          <>
            <PageTitle breadcrumbs={inwardBreadCrumbs}>Inward</PageTitle>
            <InwardPoList
              basePath={INWARD_UPROCURE_BASE_PATH}
              poDetailsPath="/UProcure/purchase-order-management/purchase-order-details"
            />
          </>
        }
      />
      <Route
        path="create-grn"
        element={
          <InwardCreateGrnGuard>
            <PageTitle breadcrumbs={inwardBreadCrumbs}>Create GRN</PageTitle>
            <InwardGrnForm basePath={INWARD_UPROCURE_BASE_PATH} />
          </InwardCreateGrnGuard>
        }
      />
      <Route
        path="grns"
        element={
          <>
            <PageTitle breadcrumbs={inwardBreadCrumbs}>Inward GRNs</PageTitle>
            <InwardGrnList basePath={INWARD_UPROCURE_BASE_PATH} />
          </>
        }
      />
      <Route
        path="grn"
        element={
          <>
            <PageTitle breadcrumbs={inwardBreadCrumbs}>GRN approval</PageTitle>
            <InwardGrnApproval basePath={INWARD_UPROCURE_BASE_PATH} />
          </>
        }
      />
      <Route
        path="report"
        element={
          <>
            <PageTitle breadcrumbs={inwardBreadCrumbs}>Inward report</PageTitle>
            <InwardReport basePath={INWARD_UPROCURE_BASE_PATH} />
          </>
        }
      />
      <Route
        path="*"
        element={<Navigate to={INWARD_UPROCURE_BASE_PATH} replace />}
      />
    </Routes>
  );
};

export default InwardPage;
